import { ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { Subscription } from 'rxjs';
import { CustomerInputFieldViewModel } from '../../shared/models';
import { InputDataType, InputValidationMaxLength, InputValidationMinLength, Prefix } from '../../shared/contentTypes';
import { IErrorMessage, ValidationErrorType } from '../../shared/validator/validatorMessage.interface';
import { CoreLanguageService } from '../../services/language.service';
import { CoreLogService } from '../../services/log.service';
import { IOSHelperService } from '../../services/IOSHelper.service';
import { KeyValueBase } from '../../shared/models/interface';
import { ValidatorsConfig } from '../../shared/validator/validator.function';
import { DEFAULT_ERRORMESSAGES, DYNAMICFIELD_ERROR_MESSAGES } from '../../shared/validator/validatorErrorMessages';

@Component({
  selector: 'ya-dynamicFormFields',
  templateUrl: './dynamicFormFields.component.html',
  styleUrls: ['./dynamicFormFields.component.scss']
})
export class DynamicFormFieldsComponent implements OnInit, OnDestroy {

  @Input() customerFieldsConfig: CustomerInputFieldViewModel[];
  @Input() initialData?: { [key: string]: string | number };
  @Output() formStatusChange = new EventEmitter<boolean>();
  @Output() formReady = new EventEmitter<void>();

  private _inputStateSubscriptions: Subscription[] = [];
  private _inputValueSubscriptions: Subscription[] = [];
  private langSubscriber: Subscription;
  private errorMessages: { [key: string]: string } = {};
  private focusedFields: { [key: string]: boolean } = {};
  private INPUT_MAX_LENGTH = 200;

  public dynamicFormGroup: FormGroup;
  public inputDataType = InputDataType;
  public type = ValidationErrorType;
  public inputMaxLength = InputValidationMaxLength;
  public prefixList: KeyValueBase[];
  public isDateValid: boolean = true;

  constructor(
    private _changeDetectorRef: ChangeDetectorRef,
    private _coreLogService: CoreLogService,
    private _coreLanguageService: CoreLanguageService,
    private _iosHelperService: IOSHelperService
  ) { }

  ngOnInit(): void {
    const me = this;
    me.prefixList = me.getPrefixListFromEnum(Prefix);
    me.buildFormControl();

    // Load initial data if provided only patch non empty values
    if (me.initialData && me.dynamicFormGroup) {
      const filteredInitialData: { [key: string]: string | number } = {};
      let hasAnyValue = false;

      Object.keys(me.initialData).forEach(key => {
        const value = me.initialData[key];
        if (value !== null && value !== undefined && value !== '') {
          filteredInitialData[key] = value;
          hasAnyValue = true;
        }
      });

      if (hasAnyValue) {
        me.dynamicFormGroup.patchValue(filteredInitialData);
        me.dynamicFormGroup.updateValueAndValidity();

        // Mark invalid fields with existing data as touched to show validation errors
        Object.keys(me.dynamicFormGroup.controls).forEach(key => {
          const control = me.dynamicFormGroup.controls[key];
          if (control.invalid && control.value) {
            control.markAsTouched();
          }
        });
      }
    }
    me.formReady.emit();
    me.PushChanges();
  }

  ngOnDestroy(): void {
    const me = this;
    if (me.langSubscriber) {
      me.langSubscriber.unsubscribe();
    }
    me._inputStateSubscriptions.forEach(sub => sub.unsubscribe());
    me._inputValueSubscriptions.forEach(sub => sub.unsubscribe());
  }

  private PushChanges(): void {
    try {
      this._changeDetectorRef.detectChanges();
    } catch (e) {
      this._coreLogService.error(
        'DynamicFormFieldsComponent.PushChanges',
        '_changeDetectorRef.detectChanges (catch)',
        e
      );
    }
  }

  /** Returns the dynamic form group */
  public get formGroup(): FormGroup {
    const me = this;
    return me.dynamicFormGroup;
  }

  /** Marks all form fields as touched to trigger validation display */
  public markAllFieldsAsTouched(): void {
    const me = this;
    if (me.dynamicFormGroup && me.dynamicFormGroup.controls) {
      Object.keys(me.dynamicFormGroup.controls).forEach(key => {
        me.dynamicFormGroup.controls[key].markAsTouched();
      });
    }
  }

  /** Returns max length for field, defaults to 200 */
  public getMaxLength(fieldName: string): number {
    const me = this;
    return me.inputMaxLength[fieldName] || this.INPUT_MAX_LENGTH;
  }

  /** Marks prefix field as touched when selection is made */
  public onPrefixSelected(): void {
    const me = this;
    me.dynamicFormGroup.controls['prefix'].markAsTouched();
  }

  /** Tracks when input field receives focus */
  public onInputFocus(fieldName: string): void {
    this.focusedFields[fieldName] = true;
  }

  /** Tracks when input field loses focus */
  public onInputBlur(fieldName: string): void {
    this.focusedFields[fieldName] = false;
  }

  public setWebView() {
    const me = this;
    me._iosHelperService.setWebviewAfterBlur();
  }

  /** Updates calendar validation state */
  public setCalendarValidation(event: boolean): void {
    const me = this;
    if (me.isDateValid) {
      me.isDateValid = event;
      me.PushChanges();
    }
  }

  /** Returns translated error message for field
   * @param fieldName Field name key
   * @return error message string
  */
  public getActiveErrorLabel(fieldName: string): string {
    const me = this;
    const errorMessage = me.errorMessages[fieldName];
    if (!errorMessage) return '';

    if (errorMessage === 'DYNAMIC_MAX_LENGTH') {
      const maxLength = me.inputMaxLength[fieldName];
      return me.loadMaxLengthLabel(maxLength);
    }
    if (errorMessage === 'DYNAMIC_MIN_LENGTH') {
      const minLength = InputValidationMinLength[fieldName];
      return me.loadMinLengthLabel(minLength);

    }
    // Translation key or already translated text
    if (errorMessage.startsWith('ph')) {
      return `${me._coreLanguageService.value(errorMessage)} `;
    }
    return `${errorMessage} `;
  }

  /**
   * Returns placeholder as function for custom components (e.g. ya-customselectinput)
   */
  public getInputFieldPlaceholder(model: CustomerInputFieldViewModel): () => string {
    const me = this;
    return () => {
      const placeholder = me._coreLanguageService.value(`phRegistrationInput_${model.fieldName}`);
      const isRequiredSuffix = (model.isRequired ? " *" : "");
      return placeholder + isRequiredSuffix;
    };
  }

  /**
   * Returns placeholder as string for input elements
   * @param model Customer input field model
   *
   */
  public getInputFieldPlaceholderString(model: CustomerInputFieldViewModel): string {
    const placeholder = this._coreLanguageService.value(`phRegistrationInput_${model.fieldName}`);
    const isRequiredSuffix = (model.isRequired ? " *" : "");
    return placeholder + isRequiredSuffix;
  }

  /** Builds form controls with validators for each field */
  private buildFormControl() {
    const me = this;
    const group: { [key: string]: FormControl } = {};
    if (me.customerFieldsConfig && me.customerFieldsConfig.length > 0) {
      for (const field of me.customerFieldsConfig) {
        const validators = ValidatorsConfig.getValidatorsForDynamicFields(field);
        const control = new FormControl('', validators);

        me._inputValueSubscriptions.push(
          control.valueChanges.subscribe(value => {
            me.updateErrorMessage(control, field.fieldName);
          })
        );
        me._inputStateSubscriptions.push(
          control.statusChanges.subscribe(status => {
            me.updateErrorMessage(control, field.fieldName);
          })
        );
        group[field.fieldName] = control;
      }
    }
    me.dynamicFormGroup = new FormGroup(group);
    me.formStatusChange.emit(me.dynamicFormGroup.valid);
  }

  /** Updates error message for field when validation state changes
   * @param control Form control to check
   * @param fieldName Field name key
  */
  private updateErrorMessage(control: FormControl, fieldName: string): void {
    const me = this;
    const messages = me.ErrorMessages(fieldName);
    const message = ValidatorsConfig.getErrorMessageKey(control, messages);
    if (me.errorMessages[fieldName] !== message) {
      me.errorMessages[fieldName] = message;
    };
    me.PushChanges();
    me.formStatusChange.emit(me.dynamicFormGroup.valid);
  }

  /** Returns error message definitions for field
   * @param fieldName Field name key
  */
  private ErrorMessages(fieldName: string): IErrorMessage[] {
    if (!fieldName) {
      return DEFAULT_ERRORMESSAGES;
    } else {
      return DYNAMICFIELD_ERROR_MESSAGES[fieldName]
    }
  }

  /** Returns translated max length message with value */
  private loadMaxLengthLabel(maxLength: InputValidationMaxLength): string {
    if (maxLength !== undefined) {
      return this._coreLanguageService.value('phDataInputs_Default_MaxLength').replace('{length}', maxLength.toString());
    } else {
      return this._coreLanguageService.value('phDataInputs_Default_MaxLength').replace('{length}', '30');
    }
  }

  /** Returns translated min length message with value */
  private loadMinLengthLabel(minLength: InputValidationMinLength): string {
    if (minLength !== undefined) {
      return this._coreLanguageService.value('phDataInputs_Default_MinLength').replace('{length}', minLength.toString());
    } else {
      return this._coreLanguageService.value('phDataInputs_Default_MinLength').replace('{length}', '3');
    }

  }

  /** Returns form values as key value pairs, excluding null/undefined
   * @return key-value pairs of form field names and their values
  */
  public getDynamicFieldsInput(): { [key: string]: string | number } {
    const values: { [key: string]: string | number } = {};
    Object.keys(this.dynamicFormGroup.controls).forEach(key => {
      const value = this.dynamicFormGroup.controls[key].value;
      if (value !== null && value !== undefined && value !== '') {
        values[key] = value;
      }
    });
    return values;
  }

  /** Generates prefix list from Prefix enum for dropdown with translated values from language service
   * @return Array of KeyValueBase with translation keys and values
  */
  private getPrefixListFromEnum(enumType: {}): KeyValueBase[] {
    const me = this;
    const result: KeyValueBase[] = [];

    for (const key in enumType) {
      if (typeof enumType[key] === "number" && key !== 'None') {
        const translationKey = `phPrefix_${key}`;
        const keyValueBase: KeyValueBase = {
          key: translationKey,
          value: me._coreLanguageService.value(translationKey)
        };
        result.push(keyValueBase);
      }
    }
    return result;
  }

  /** Handles Enter key to remove focus from input fields */
  public handleEnterKey(event) {
    if (event.keyCode === 13) {
      // to avoid focus on input after closing the modal dialog
      const form: HTMLElement = document.getElementById("DynamicDataForm");
      form.classList.remove("mat-focused");
    }
  }
}
